/**
 * CreatorTab — Phase A landing view for the author's creator identity.
 *
 * MVP scope:
 *   - If no author → empty state with "Setup author" CTA + Import button
 *   - If author exists → toolbar (Import / Export / Redo) + identity editor,
 *     audiences chip, and pillars. Platform setup lives in the sibling
 *     Platforms sub-tab.
 *
 * Export → AuthorExportService.exportSelfAuthor(projectId, authorId) → JSON download.
 * Import → AuthorImportDialog (validates + replaces this author workspace).
 *
 * Testid: content-plan-creator-tab
 */

import React, { useCallback, useState } from 'react';
import { Alert, Box, Button, Chip, Paper, Snackbar, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { useAuthor } from '../../hooks/useAuthor';
import { TopicPillarsSection } from '../pillars/TopicPillarsSection';
import { AuthorIdentityEditor } from './AuthorIdentityEditor';
import { AuthorImportDialog } from './AuthorImportDialog';
import { AuthorExportService, authorExportService } from '../../services/AuthorExportService';

const TESTID = 'content-plan-creator-tab';

export interface CreatorTabProps {
  projectId: string;
  /** Current user id — propagated to partial-mode endpoints. */
  userId: string;
  onOnboardClick: () => void;
  onAddAuthorClick: () => void;
  /** Redo flow — opens AuthorOnboardingDialog with existingAuthorId set. */
  onRedoClick: (authorId: string) => void;
  /** Notified when a partial-mode task starts so a parent task tray can subscribe. */
  onTaskStarted?: (taskId: string, label: string) => void;
}

export function CreatorTab(props: CreatorTabProps) {
  const { projectId, userId, onOnboardClick, onAddAuthorClick, onRedoClick, onTaskStarted } = props;
  const { author, loading } = useAuthor(projectId);

  // Import / Export state
  const [importOpen, setImportOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>(
    { open: false, message: '', severity: 'success' },
  );

  const handleImportClick = useCallback(() => {
    setImportOpen(true);
  }, []);

  const handleImportSuccess = useCallback((summary: { displayName: string; postCount: number }) => {
    setSnackbar({
      open: true,
      message: `Imported "${summary.displayName}" with ${summary.postCount} posts.`,
      severity: 'success',
    });
  }, []);

  const handleExportClick = useCallback(async () => {
    try {
      const envelope = await authorExportService.exportSelfAuthor(projectId, author?.id);
      AuthorExportService.download(
        envelope,
        AuthorExportService.buildFilename(envelope.metadata.displayName),
      );
      setSnackbar({
        open: true,
        message: `Exported "${envelope.metadata.displayName}".`,
        severity: 'success',
      });
    } catch (err) {
      console.error('[CreatorTab] export failed', err);
      setSnackbar({
        open: true,
        message: `Export failed: ${err instanceof Error ? err.message : String(err)}`,
        severity: 'error',
      });
    }
  }, [projectId]);

  const renderToolbar = (showExport: boolean, showRedo: boolean) => (
    <Stack direction="row" justifyContent="flex-end" spacing={1}>
      <Button
        variant="contained"
        size="small"
        onClick={onAddAuthorClick}
        startIcon={<AddIcon />}
        data-testid={`${TESTID}-add-author`}
      >
        Add author
      </Button>
      <Button
        variant="outlined"
        size="small"
        onClick={handleImportClick}
        startIcon={<FileUploadIcon />}
        data-testid={`${TESTID}-import-creator`}
      >
        Import creator
      </Button>
      {showExport && (
        <Button
          variant="outlined"
          size="small"
          onClick={handleExportClick}
          startIcon={<FileDownloadIcon />}
          data-testid={`${TESTID}-export-creator`}
        >
          Export creator
        </Button>
      )}
      {showRedo && author && (
        <Button
          variant="outlined"
          size="small"
          onClick={() => onRedoClick(author.id ?? '')}
          data-testid={`${TESTID}-redo-onboarding`}
        >
          Redo onboarding
        </Button>
      )}
    </Stack>
  );

  const dialogAndSnackbar = (
    <>
      <AuthorImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        projectId={projectId}
        currentAuthor={author}
        onSuccess={handleImportSuccess}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );

  if (loading) {
    return (
      <Box data-testid={TESTID} sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Loading author profile...
        </Typography>
      </Box>
    );
  }

  if (!author) {
    return (
      <Stack spacing={2} data-testid={TESTID} sx={{ p: 2 }}>
        {renderToolbar(false, false)}
        <Box sx={{ p: 1 }}>
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              No author profile yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Set up your identity so AI can generate content in your voice.
              <br />
              Works with any LinkedIn / X profile URL, or paste content manually.
              <br />
              Or import a creator export (.json) from another project.
            </Typography>
            <Button variant="contained" size="large" onClick={onOnboardClick}>
              Setup author profile
            </Button>
          </Paper>
        </Box>
        {dialogAndSnackbar}
      </Stack>
    );
  }

  return (
    <Stack spacing={2} data-testid={TESTID} sx={{ p: 2 }}>
      {!author.isComplete && (
        <Alert severity="warning">
          Author profile is incomplete — some fields are missing. AI generation
          quality depends on these. Click "Redo onboarding" to refresh, or edit
          fields manually (coming in a follow-up).
        </Alert>
      )}

      {/* Author identity card with full editor + Re-analyze identity action */}
      {renderToolbar(true, true)}

      <AuthorIdentityEditor
        projectId={projectId}
        userId={userId}
        author={author}
        onTaskStarted={onTaskStarted}
      />

      {Array.isArray(author.targetAudienceIds) && author.targetAudienceIds.length > 0 && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Target audiences
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {author.targetAudienceIds.map((id) => (
              <Chip key={id} label={id} size="small" variant="outlined" />
            ))}
          </Stack>
        </Paper>
      )}

      <TopicPillarsSection
        projectId={projectId}
        authorId={author.id ?? null}
        userId={userId}
        onTaskStarted={onTaskStarted}
      />

      {dialogAndSnackbar}
    </Stack>
  );
}
